import { useState, useCallback } from 'react';
import { Modal } from 'antd';
import { useSelector } from 'react-redux';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';
import { useNotifications } from '../../../hooks/useNotifications';
import { IncidentsService } from '../../../services/incidentsService';
import { HeadquartersService } from '../../../services/headquartersService';
import { useDebouncedFunction } from '../../../hooks/useDebouncedFunction';
import { FinalCustomersService } from '../../../services/finalCustomersService';
import { getFullName } from '../../../utility/finalCustomerUtils';
import { debounceFunction } from '../../../utility/utility';
import { finalCustomerSolutionIdRules } from '../../../config/constants/finalCustomerSolutionRuleConstants';

export const useServiceData = ({ serviceId, defaultData, newData: dataToOverwrite } = {}) => {
  const [serviceData, setServiceData] = useState();
  const [isLoading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { translate: t } = useDictionaryTranslation();
  const { showErrorNotification, showSuccessNotification, showConfirmNotification } = useNotifications();
  const { enableIncidentsOffschedule, solutionId } = useSelector((state) => ({
    enableIncidentsOffschedule: state.auth.accountData.enableIncidentsOffschedule,
    solutionId: state.auth?.accountData?.solutionId,
  }));
  const finalCustomerSolutionRules = finalCustomerSolutionIdRules[solutionId] ?? finalCustomerSolutionIdRules.default;

  const errorMessages = {
    [IncidentsService.USER_NOT_AVAILABLE]: 'userNotAvailable',
    [IncidentsService.PATIENT_HAS_OVERLAPS]: 'patientHasOverlaps',
    [IncidentsService.ASSET_HAS_OVERLAPS]: 'assetHasOverlaps',
    [IncidentsService.USER_HAS_OVERLAPS]: 'userHasOverlaps',
    [IncidentsService.ASSET_NOT_AVAILABLE]: 'assetNotAvailable',
    [IncidentsService.USER_CANNOT_PERFORM_SERVICE]: 'userCannotPerformService',
    [IncidentsService.USER_NOT_ASSIGNED_TO_HEADQUARTER]: 'userNotAssignedToHeadquarter',
    [IncidentsService.SERVICE_TYPE_NOT_ASSIGNED_TO_HEADQUARTER]: 'serviceTypeNotAssignedToHeadquarter',
  };

  const checkError = (message) => {
    if (message == null) {
      return false;
    }
    const errorMessage = errorMessages[message];
    if (errorMessage == null) {
      return false;
    }
    Modal.error({
      title: t('cantSaveRecord'),
      content: t(errorMessage),
    });
    return true;
  };

  const askToConfirmOffScheduleAssignation = ({ errorMessage, onConfirm }) => {
    return new Promise((resolve, reject) => {
      showConfirmNotification({
        title: t('incidentOutsideSchedule'),
        content: t(errorMessage),
        onOk: async () => {
          await onConfirm();
          resolve();
        },
        onCancel: () => {
          reject();
        },
        okText: t('assignIncidentOffSchedule'),
        cancelText: t('cancel'),
      });
    });
  };

  const updateConfirmService = async (data) => {
    setLoading(true);
    try {
      await IncidentsService.updateService(serviceId, data);
      showSuccessNotification({
        title: 'success',
        description: 'successUpdatingRecord',
      });
    } catch (error) {
      const message = error?.response?.data?.message;
      if (!checkError(message)) {
        showErrorNotification({
          title: 'error',
          description: 'errorUpdatingRecord',
        });
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const checkAvailabilityError = async (message, handler) => {
    if (!enableIncidentsOffschedule || !IncidentsService.availabilityErrors.has(message)) {
      return false;
    }
    const errorMessage = errorMessages[message];
    await askToConfirmOffScheduleAssignation({
      errorMessage,
      onConfirm: handler,
    });
    return true;
  };

  const updateService = async (data, newServiceId, showMessage = true) => {
    setLoading(true);
    try {
      await IncidentsService.updateService(newServiceId ?? serviceId, data);
      if (showMessage) {
        showSuccessNotification({
          title: 'success',
          description: 'successUpdatingRecord',
        });
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      const handledAvailabilityError = await checkAvailabilityError(message, () =>
        updateService({ ...data, forceAssignation: true }),
      );
      if (!handledAvailabilityError) {
        if (!checkError(message)) {
          showErrorNotification({
            title: 'error',
            description: 'errorUpdatingRecord',
          });
        }
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const createService = async (data) => {
    setLoading(true);
    try {
      const dataIncident = await IncidentsService.createService(data);
      showSuccessNotification({
        title: 'success',
        description: 'successCreatingRecord',
      });
      return dataIncident;
    } catch (error) {
      const message = error?.response?.data?.message;
      const handledAvailabilityError = await checkAvailabilityError(message, () =>
        createService({ ...data, forceAssignation: true }),
      );
      if (!handledAvailabilityError) {
        if (!checkError(message)) {
          showErrorNotification({
            title: 'error',
            description: 'errorCreatingRecord',
          });
        }
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const getDefaultData = useCallback(async (dataToAssign) => {
    setLoading(true);
    const keysMap = {
      professional: 'idUser',
      serviceType: 'idTypeService',
      start: 'start',
      headquarter: 'idHeadquarter',
      finalCustomer: 'idFinalCustomer',
    };
    const newData = Object.entries(dataToAssign).reduce((acc, [key, value]) => {
      const keyData = keysMap[key];
      if (keyData && value) {
        acc[keyData] = value;
      }
      return acc;
    }, {});
    try {
      if (dataToAssign.headquarter) {
        const { headquarter } = await HeadquartersService.getHeadquarterById(
          dataToAssign.headquarter._id ?? dataToAssign.headquarter,
        );
        newData.idHeadquarter = headquarter;
        newData.address = headquarter.address;
      }
      if (dataToAssign.idFinalCustomer) {
        const finalCustomer = await FinalCustomersService.getById(
          dataToAssign.idFinalCustomer._id ?? dataToAssign.idFinalCustomer,
        );
        finalCustomer.fullname = getFullName(finalCustomer, {
          useFullName: finalCustomerSolutionRules.showName1,
        });
        newData.idFinalCustomer = finalCustomer;
      }
    } finally {
      setLoading(false);
    }
    return newData;
  }, []);

  const getServiceData = useCallback(
    debounceFunction(async () => {
      if (!serviceId) {
        const defaultServiceData = await getDefaultData({
          ...defaultData,
          ...dataToOverwrite,
        });
        setServiceData(defaultServiceData);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const service = await IncidentsService.getById(serviceId);
        const overwriteData = await getDefaultData({
          ...dataToOverwrite,
        });
        setServiceData({
          ...service,
          ...overwriteData,
        });
      } catch (error) {
        showErrorNotification({
          title: 'error',
          description: 'errorRetrievingRecord',
        });
        setHasError(true);
      } finally {
        setLoading(false);
      }
    }, 200),
    [serviceId, showErrorNotification, getDefaultData, dataToOverwrite, defaultData, dataToOverwrite],
  );

  useDebouncedFunction(getServiceData, 100);

  return {
    updateService,
    createService,
    isLoading,
    serviceData,
    hasError,
    updateConfirmService,
    getServiceData,
  };
};
